import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { AiChatMessage, AiConfirmValues, AiLeadResult, AiOpenLeadRequest } from '../../models/ai-assistant.models';

/** Confirm event carrying the message plus the user's inline field picks (dropdowns / reason). */
export interface AiConfirmEvent {
  message: AiChatMessage;
  values?: AiConfirmValues;
}

/** A prose choice question parsed into its selectable options and the lead-in question text. */
interface ParsedChoice {
  options: string[];
  question: string;
}
import { AiAssistantActionCardComponent } from '../ai-assistant-action-card/ai-assistant-action-card.component';
import { AiAssistantViewsComponent } from '../ai-assistant-views/ai-assistant-views.component';
import { AiAssistantRequiredFieldsComponent, ProvidedField } from '../ai-assistant-required-fields/ai-assistant-required-fields.component';

/**
 * Renders the chat thread: user/assistant bubbles, the assistant "typing…" placeholder,
 * the inline confirm card for a pending write, and a "View in Leads" affordance after a
 * successful create/update. Auto-scrolls to the latest message.
 */
@Component({
  selector: 'app-ai-assistant-message-list',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, AiAssistantActionCardComponent, AiAssistantViewsComponent, AiAssistantRequiredFieldsComponent],
  templateUrl: './ai-assistant-message-list.component.html',
  styleUrls: ['./ai-assistant-message-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantMessageListComponent {
  @ViewChild('scroller') private scroller?: ElementRef<HTMLElement>;

  private _messages: AiChatMessage[] = [];
  @Input() set messages(value: AiChatMessage[]) {
    this._messages = value || [];
    this.scheduleScroll();
  }
  get messages(): AiChatMessage[] {
    return this._messages;
  }

  @Output() confirmAction = new EventEmitter<AiConfirmEvent>();
  @Output() cancelAction = new EventEmitter<AiChatMessage>();
  /** Open a lead; carries the lead's `project_id` (Round 5) so the panel can open its project. */
  @Output() viewLead = new EventEmitter<AiOpenLeadRequest>();
  /** Open a lead straight on its Appointment tab (from an appointment row). */
  @Output() viewLeadAppointments = new EventEmitter<string>();
  @Output() retry = new EventEmitter<AiChatMessage>();
  /** A follow-up prompt from a view (pagination "show more", capability example, view switch). */
  @Output() pickPrompt = new EventEmitter<string>();
  /** The user filled the missing required fields inline — relay the selections to the assistant. */
  @Output() provideFields = new EventEmitter<ProvidedField[]>();

  trackById(_index: number, message: AiChatMessage): string {
    return message.id;
  }

  trackByLeadId(_index: number, lead: AiLeadResult): string {
    return lead.id;
  }

  // ---- Inline options picker --------------------------------------------------
  // When the assistant asks a choice question in plain prose (e.g. "What is the
  // reason for closing this lead? Your options are: Price, Location, ….") the
  // options are buried in the text and the user has to type. We parse that list
  // and offer a dropdown so they can pick instead — the selection is sent back as
  // their reply (same channel as any follow-up prompt).

  /** The user's current pick per message, keyed by message id. */
  optionPicks: Record<string, string> = {};
  /** Messages already answered via the picker — disables re-sending the same one. */
  private answeredOptions = new Set<string>();
  /** Parse cache keyed by `id|length` so we don't re-run the regex every change-detection pass. */
  private optionCache = new Map<string, ParsedChoice | null>();

  /**
   * Options to offer as a dropdown for an assistant message, or `null` when it isn't a
   * choice question. Only a plain-text question (no confirm card / structured views / lead
   * cards) qualifies, so structured flows keep their own richer pickers.
   */
  inlineOptions(message: AiChatMessage): string[] | null {
    return this.parsedChoice(message)?.options ?? null;
  }

  /**
   * The message text to render. While the picker is active on the latest message we show only the
   * question (e.g. "…What is the reason for closing this lead?") and drop the long "options are: …"
   * list, since the dropdown replaces it. All other messages render their full content.
   */
  displayText(message: AiChatMessage): string {
    const parsed = this.parsedChoice(message);
    if (parsed && parsed.question && this.isLastMessage(message)) {
      return parsed.question;
    }
    return message.content || '';
  }

  private parsedChoice(message: AiChatMessage): ParsedChoice | null {
    if (!message || message.role !== 'assistant' || message.pending || message.errored) {
      return null;
    }
    if (message.pendingAction || message.views?.length || message.leads?.length) {
      return null;
    }
    const text = message.content || '';
    if (!text) {
      return null;
    }
    const key = `${message.id}|${text.length}`;
    if (this.optionCache.has(key)) {
      return this.optionCache.get(key) as ParsedChoice | null;
    }
    const parsed = this.parseOptions(text);
    this.optionCache.set(key, parsed);
    return parsed;
  }

  /** Extract an enumerated option list (and the question before it) from prose; needs ≥2 options. */
  private parseOptions(text: string): ParsedChoice | null {
    const trigger =
      /(?:\b(?:your |the |these |available |possible |valid )?options are|choose from|choose one of|select from|select one of|pick one of|reasons?(?: are| include))\s*:?\s*/i;
    const match = trigger.exec(text);
    if (!match || match.index === undefined) {
      return null;
    }
    let rest = text.slice(match.index + match[0].length).trim();
    rest = rest.replace(/[.?!]+\s*$/, ''); // drop the sentence terminator
    if (!rest) {
      return null;
    }
    let parts = rest.split(/,\s*/);
    // Handle a comma-free "A or B" tail (or a single "X or Y" with no commas at all).
    if (parts.length === 1) {
      parts = rest.split(/\s+or\s+/i);
    }
    const seen = new Set<string>();
    const options = parts
      .map(p =>
        p
          .replace(/^(?:or|and)\s+/i, '')
          .replace(/[.?!]+$/, '')
          .trim()
      )
      .filter(Boolean)
      .filter(p => {
        const k = p.toLowerCase();
        if (seen.has(k)) {
          return false;
        }
        seen.add(k);
        return true;
      });
    if (options.length < 2) {
      return null;
    }
    // The question is everything before the trigger, minus any dangling lead-in word.
    const question = text
      .slice(0, match.index)
      .replace(/\b(?:your|the|these|available|possible|valid)\s*$/i, '')
      .trim();
    return { options, question };
  }

  /** Only the latest message stays interactive, so stale questions above can't be re-answered. */
  isLastMessage(message: AiChatMessage): boolean {
    const last = this.messages[this.messages.length - 1];
    return !!last && last.id === message.id;
  }

  optionSent(message: AiChatMessage): boolean {
    return this.answeredOptions.has(message.id);
  }

  /** Send the picked option back to the assistant as the user's reply. */
  sendOption(message: AiChatMessage): void {
    const value = (this.optionPicks[message.id] || '').trim();
    if (!value || this.answeredOptions.has(message.id)) {
      return;
    }
    this.answeredOptions.add(message.id);
    this.pickPrompt.emit(value);
  }

  /**
   * Open a lead from a result card. With an id it deep-links to `/leads?id=<id>`; without
   * one (e.g. a text-parsed card) it falls back to the leads list via the empty-id handler.
   */
  openLead(lead: AiLeadResult): void {
    this.viewLead.emit({ leadId: lead?.id || '', projectId: lead?.project_id ?? null });
  }

  /** Initials for the card avatar, e.g. "Shane McCarthy" → "SM". */
  initials(lead: AiLeadResult): string {
    const parts = (lead?.name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '?';
    }
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }

  /** Card subtitle: lead type + project, e.g. "Buyer · East Village Vila B". */
  leadSubtitle(lead: AiLeadResult): string {
    return [lead?.type, lead?.project].filter(Boolean).join(' · ');
  }

  /** CSS modifier for the temperature pill (Hot / Warm / Cold). */
  temperatureClass(lead: AiLeadResult): string {
    const temp = (lead?.temperature || '').toLowerCase();
    if (temp === 'hot') return 'ai-lead-card__temp--hot';
    if (temp === 'warm') return 'ai-lead-card__temp--warm';
    if (temp === 'cold') return 'ai-lead-card__temp--cold';
    return '';
  }

  telHref(phone?: string): string {
    return 'tel:' + (phone || '').replace(/\s+/g, '');
  }

  mailtoHref(email?: string): string {
    return 'mailto:' + (email || '').trim();
  }

  /** Lead id to surface an "Open Lead" button after a confirmed write. */
  resultLeadId(message: AiChatMessage): string | null {
    return message.actionResult?.ok && message.actionResult?.lead_id ? message.actionResult.lead_id : null;
  }

  /**
   * Missing required fields after a create that was blocked by business rules — rendered as a
   * structured checklist so the user can see exactly what to supply. Backend already returns
   * these on `action_result.required_fields` (no backend change needed).
   */
  requiredFields(message: AiChatMessage): string[] {
    const result = message.actionResult;
    return result && result.ok === false && result.required_fields?.length ? result.required_fields : [];
  }

  /** Turn a snake_case field key into a readable label, e.g. `mobile_phone` → `Mobile phone`. */
  humanizeField(field: string): string {
    const text = (field || '').replace(/_/g, ' ').trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  private scheduleScroll(): void {
    // Defer until the new bubble is painted.
    setTimeout(() => {
      const el = this.scroller?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
